import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { LEGAL_UPDATED_AT } from "../lib/legalMeta";

/**
 * Generic legal modal trigger used for Terms / Privacy / Disclosures.
 * Pulls all copy from i18n under the supplied namespace (e.g. `legal_terms`).
 *
 * Expected i18n keys (per language):
 *   <ns>.link        – footer link label
 *   <ns>.tag         – modal eyebrow tag
 *   <ns>.title       – modal title
 *   <ns>.sections    – array of { h: string, p: string }
 *
 * The "Last updated · {date}" line is rendered automatically from
 * LEGAL_UPDATED_AT and formatted in the active locale.
 */
export default function LegalInfo({ ns, testid, children }) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);

  const sections = t(`${ns}.sections`, { returnObjects: true }) || [];

  const formattedDate = new Intl.DateTimeFormat(i18n.language || "en", {
    year: "numeric", month: "long", day: "numeric",
  }).format(new Date(LEGAL_UPDATED_AT));
  const updatedLine = t("legal_meta.updated", { date: formattedDate });

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid={`footer-${testid}-link`}
        className="text-sm text-ink2 hover:text-ink underline-offset-4 underline transition-colors text-left bg-transparent border-0 p-0 cursor-pointer"
      >
        {children || t(`${ns}.link`)}
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center px-4 py-8 bg-ink/70 backdrop-blur-sm"
          onClick={() => setOpen(false)}
          data-testid={`${testid}-info-overlay`}
        >
          <div
            className="relative w-full max-w-3xl bg-bg border border-ink shadow-2xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="border-b border-line px-6 py-4 flex items-center justify-between sticky top-0 bg-bg">
              <div>
                <div className="label-tag">{t(`${ns}.tag`)}</div>
                <div className="font-serif text-2xl mt-1">{t(`${ns}.title`)}</div>
                <div className="text-[11px] font-mono text-ink2 mt-1">{updatedLine}</div>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                data-testid={`${testid}-info-close`}
                aria-label="Close"
                className="text-ink2 hover:text-ink text-2xl leading-none px-2"
              >
                ×
              </button>
            </div>

            <div className="px-6 py-6 space-y-5 text-sm text-ink2 leading-relaxed">
              {Array.isArray(sections) && sections.map((s) => (
                <div key={s.h} className="border border-line bg-surface p-4">
                  <div className="font-serif text-ink text-lg">{s.h}</div>
                  <p className="mt-2 whitespace-pre-line">{s.p}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
